import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, MapPinIcon, CalendarIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { APIProvider, Map, AdvancedMarker } from '@vis.gl/react-google-maps';
import { useSelector } from 'react-redux';

const GOOGLE_MAPS_API_KEY = 'AIzaSyDHeASGVySF_J2T1ctEiAkVUdv9oBCgKjI';

const EstateActions = ({ estate, t, isAuthenticated }) => {
    const [isMapOpen, setIsMapOpen] = useState(false);
    const [isContactModalOpen, setIsContactModalOpen] = useState(false);
    const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
    const [contactForm, setContactForm] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        message: ''
    });
    const [bookingForm, setBookingForm] = useState({
        viewing_date: '',
        notes: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');

    const user = useSelector(state => state.auth.user);
    const location = estate._geoloc || { lat: 40.4637, lng: -3.7492 };

    // Function to get approximate location for unauthenticated users
    const getApproximateLocation = (location) => {
        if (isAuthenticated) return location;

        // Offset by random amount between 200-300 meters
        const metersToLatitude = 0.00001; // Roughly 1.1 meters
        const metersToLongitude = 0.00001 / Math.cos(location.lat * Math.PI / 180);

        const offsetMeters = Math.random() * (300 - 200) + 200; // Random between 200-300
        const latOffset = (Math.random() - 0.5) * 2 * (offsetMeters * metersToLatitude);
        const lngOffset = (Math.random() - 0.5) * 2 * (offsetMeters * metersToLongitude);

        return {
            lat: location.lat + latOffset,
            lng: location.lng + lngOffset
        };
    };

    const mapLocation = getApproximateLocation(location);

    const BlueMarker = () => (
        isAuthenticated ? (
            <div className="size-6 bg-sky-600 rounded-full border-2 border-white shadow-lg" />
        ) : (
            <div className="size-36 bg-red-500/20 border-2 border-red-500/40 rounded-full">
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-xs text-red-700 bg-white px-2 py-1 rounded-full shadow-lg">
                        Omtrentlig placering
                    </div>
                </div>
            </div>
        )
    );

    const handleContactChange = (e) => {
        const { name, value } = e.target;
        setContactForm(prev => ({ ...prev, [name]: value }));
    };

    const handleBookingChange = (e) => {
        const { name, value } = e.target;
        setBookingForm(prev => ({ ...prev, [name]: value }));
    };

    const handleContactSubmit = async (e) => {
        e.preventDefault();
        if (!isAuthenticated) {
            setFormError(t('estate.details.actions.loginRequired'));
            return;
        }

        setIsSubmitting(true);
        setFormError('');
        setFormSuccess('');

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/estate-enquiries`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    estate_id: estate.id,
                    ...contactForm
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'An error occurred');
            }

            setFormSuccess(t('estate.details.actions.contactSuccess'));
            setContactForm({
                first_name: '',
                last_name: '',
                email: '',
                phone: '',
                message: ''
            });

            // Close modal after 2 seconds
            setTimeout(() => {
                setIsContactModalOpen(false);
                setFormSuccess('');
            }, 2000);
        } catch (error) {
            setFormError(error.message || t('estate.details.actions.contactError'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBookingSubmit = async (e) => {
        e.preventDefault();
        if (!isAuthenticated) {
            setFormError(t('estate.details.actions.loginRequired'));
            return;
        }

        setIsSubmitting(true);
        setFormError('');
        setFormSuccess('');

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/estate-viewings`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    estate_id: estate.id,
                    ...bookingForm
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'An error occurred');
            }

            setFormSuccess(t('estate.details.actions.bookingSuccess'));
            setBookingForm({
                viewing_date: '',
                notes: ''
            });

            // Close modal after 2 seconds
            setTimeout(() => {
                setIsBookingModalOpen(false);
                setFormSuccess('');
            }, 2000);
        } catch (error) {
            setFormError(error.message || t('estate.details.actions.bookingError'));
        } finally {
            setIsSubmitting(false);
        }
    };

    // Pre-fill form with user data if available
    useEffect(() => {
        if (user && isAuthenticated) {
            setContactForm(prev => ({
                ...prev,
                first_name: user.first_name || '',
                last_name: user.last_name || '',
                email: user.email || '',
                phone: user.phone || ''
            }));
        }
    }, [user, isAuthenticated]);

    return (
        <>
            <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                    {estate?.tour && (
                        <a
                            href={estate.tour}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block w-full bg-sky-600 text-white px-4 py-3 rounded-lg hover:bg-sky-700 transition-colors font-semibold text-center"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="size-6 inline mr-3"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="m21 7.5-9-5.25L3 7.5m18 0-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9"
                                />
                            </svg>
                            3D Tour
                        </a>
                    )}

                    <button
                        onClick={() => setIsMapOpen(true)}
                        className="block w-full bg-sky-600 text-white px-4 py-3 rounded-lg hover:bg-sky-700 transition-colors font-semibold text-center"
                    >
                        <MapPinIcon className="size-6 inline mr-3" />
                        {t('estate.details.actions.showMap')}
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <button
                        onClick={() => setIsContactModalOpen(true)}
                        className="w-full bg-white text-sky-600 px-4 py-3 rounded-lg border border-sky-600 hover:bg-sky-50 transition-colors"
                    >
                        <EnvelopeIcon className="size-6 inline mr-3" />
                        {t('estate.details.actions.contact')}
                    </button>

                    <button
                        onClick={() => setIsBookingModalOpen(true)}
                        className="w-full bg-white text-sky-600 px-4 py-3 rounded-lg border border-sky-600 hover:bg-sky-50 transition-colors"
                    >
                        <CalendarIcon className="size-6 inline mr-3" />
                        {t('estate.details.actions.booking')}
                    </button>
                </div>
            </div>

            {/* Map Modal */}
            <Dialog
                open={isMapOpen}
                onClose={() => setIsMapOpen(false)}
                className="relative z-50"
            >
                <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <Dialog.Panel className="w-full max-w-4xl bg-white rounded-xl shadow-xl">
                        <div className="relative">
                            <button
                                onClick={() => setIsMapOpen(false)}
                                className="absolute right-4 top-4 z-10 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100"
                            >
                                <XMarkIcon className="h-6 w-6" />
                            </button>

                            <div className="h-96 w-full rounded-xl overflow-hidden">
                                <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                                    <Map
                                        mapId="8f541c90bb5b58e2"
                                        center={mapLocation}
                                        defaultZoom={isAuthenticated ? 15 : 14}
                                        className="w-full h-full"
                                        options={{
                                            mapTypeControl: true,
                                            fullscreenControl: false,
                                            streetViewControl: isAuthenticated,
                                            zoomControl: true,
                                            scrollwheel: true,
                                            gestureHandling: "greedy",
                                            maxZoom: isAuthenticated ? undefined : 14, // Prevent zooming in too close for unauthenticated users
                                        }}
                                    >
                                        <AdvancedMarker position={mapLocation}>
                                            <BlueMarker />
                                        </AdvancedMarker>
                                    </Map>
                                </APIProvider>
                            </div>

                            <div className="p-6">
                                <Dialog.Title className="text-lg font-semibold mb-2">
                                    {estate.title}
                                </Dialog.Title>
                                {isAuthenticated && (
                                    <p className="text-gray-600">
                                        {estate.address_city}
                                    </p>
                                )}
                                {!isAuthenticated && (
                                    <p className="text-sm text-red-600 mt-2">
                                        Log ind for at se mere nøjagtig placering
                                    </p>
                                )}
                            </div>
                        </div>
                    </Dialog.Panel>
                </div>
            </Dialog>

            {/* Contact Modal */}
            <Dialog
                open={isContactModalOpen}
                onClose={() => setIsContactModalOpen(false)}
                className="relative z-50"
            >
                <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <Dialog.Panel className="w-full max-w-md bg-white rounded-xl shadow-xl">
                        <div className="relative">
                            <button
                                onClick={() => setIsContactModalOpen(false)}
                                className="absolute right-4 top-4 z-10 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100"
                            >
                                <XMarkIcon className="h-6 w-6" />
                            </button>

                            <div className="p-6">
                                <Dialog.Title className="text-lg font-semibold mb-4">
                                    {t('estate.details.actions.contactTitle')}
                                </Dialog.Title>

                                <p className="text-gray-600 mb-6">
                                    {t('estate.details.actions.contactDescription')}
                                </p>

                                {formError && (
                                    <div className="bg-red-50 text-red-700 p-3 rounded-md mb-4">
                                        {formError}
                                    </div>
                                )}

                                {formSuccess && (
                                    <div className="bg-green-50 text-green-700 p-3 rounded-md mb-4">
                                        {formSuccess}
                                    </div>
                                )}

                                <form onSubmit={handleContactSubmit} className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
                                                {t('estate.details.actions.firstName')} *
                                            </label>
                                            <input
                                                type="text"
                                                id="first_name"
                                                name="first_name"
                                                value={contactForm.first_name}
                                                onChange={handleContactChange}
                                                required
                                                className="w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
                                                {t('estate.details.actions.lastName')} *
                                            </label>
                                            <input
                                                type="text"
                                                id="last_name"
                                                name="last_name"
                                                value={contactForm.last_name}
                                                onChange={handleContactChange}
                                                required
                                                className="w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                            {t('estate.details.actions.email')} *
                                        </label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            value={contactForm.email}
                                            onChange={handleContactChange}
                                            required
                                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                                            {t('estate.details.actions.phone')} *
                                        </label>
                                        <input
                                            type="tel"
                                            id="phone"
                                            required
                                            name="phone"
                                            value={contactForm.phone}
                                            onChange={handleContactChange}
                                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                                            {t('estate.details.actions.message')} *
                                        </label>
                                        <textarea
                                            id="message"
                                            name="message"
                                            value={contactForm.message}
                                            onChange={handleContactChange}
                                            required
                                            rows={4}
                                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                                        />
                                    </div>

                                    <div className="pt-2">
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="w-full bg-sky-600 text-white px-4 py-2 rounded-md hover:bg-sky-700 transition-colors disabled:bg-sky-300"
                                        >
                                            {isSubmitting ? t('estate.details.actions.sending') : t('estate.details.actions.send')}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </Dialog.Panel>
                </div>
            </Dialog>

            {/* Booking Modal */}
            <Dialog
                open={isBookingModalOpen}
                onClose={() => setIsBookingModalOpen(false)}
                className="relative z-50"
            >
                <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <Dialog.Panel className="w-full max-w-md bg-white rounded-xl shadow-xl">
                        <div className="relative">
                            <button
                                onClick={() => setIsBookingModalOpen(false)}
                                className="absolute right-4 top-4 z-10 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100"
                            >
                                <XMarkIcon className="h-6 w-6" />
                            </button>

                            <div className="p-6">
                                <Dialog.Title className="text-lg font-semibold mb-4">
                                    {t('estate.details.actions.bookingTitle')}
                                </Dialog.Title>

                                <p className="text-gray-600 mb-6">
                                    {t('estate.details.actions.bookingDescription')}
                                </p>

                                {formError && (
                                    <div className="bg-red-50 text-red-700 p-3 rounded-md mb-4">
                                        {formError}
                                    </div>
                                )}

                                {formSuccess && (
                                    <div className="bg-green-50 text-green-700 p-3 rounded-md mb-4">
                                        {formSuccess}
                                    </div>
                                )}

                                <form onSubmit={handleBookingSubmit} className="space-y-4">
                                    <div>
                                        <label htmlFor="viewing_date" className="block text-sm font-medium text-gray-700 mb-1">
                                            {t('estate.details.actions.viewingDate')} *
                                        </label>
                                        <input
                                            type="datetime-local"
                                            id="viewing_date"
                                            name="viewing_date"
                                            value={bookingForm.viewing_date}
                                            onChange={handleBookingChange}
                                            required
                                            min={new Date().toISOString().slice(0, 16)}
                                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                                            {t('estate.details.actions.notes')}
                                        </label>
                                        <textarea
                                            id="notes"
                                            name="notes"
                                            value={bookingForm.notes}
                                            onChange={handleBookingChange}
                                            rows={4}
                                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                                        />
                                    </div>

                                    <div className="pt-2">
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="w-full bg-sky-600 text-white px-4 py-2 rounded-md hover:bg-sky-700 transition-colors disabled:bg-sky-300"
                                        >
                                            {isSubmitting ? t('estate.details.actions.sending') : t('estate.details.actions.book')}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </Dialog.Panel>
                </div>
            </Dialog>
        </>
    );
};

export default EstateActions;
